import { useState, useMemo, type HTMLAttributes } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { SingleItemAutocomplete } from '~components/autocomplete/single-item-autocomplete'
import {
  useGetContinentLocations,
  useGetCountryLocations,
  type ContinentLocation,
  type CountryLocation,
} from '~hooks/queries/port-info/use-get-locations'
import { inputWidth } from '../../add-visit-flow'
import type { ContinentSelection, CountrySelection } from '../../../../types'

interface ContinentOption {
  label: string
  continent: ContinentLocation
}

interface CountryOption {
  label: string
  country: CountryLocation
}

interface ContinentAutocompleteProps {
  value?: ContinentSelection
  onOptionChange: (value: ContinentSelection | undefined) => void
  error?: boolean
}

export function ContinentAutocomplete({
  value,
  onOptionChange,
  error = false,
}: ContinentAutocompleteProps) {
  const [, setInputValue] = useState(value?.continentName || '')

  const { data: continents, isLoading } = useGetContinentLocations()

  const options: ContinentOption[] = useMemo(() => {
    return (
      continents?.map((continent) => ({
        label: continent.ContinentName,
        continent,
      })) || []
    )
  }, [continents])

  const selectedOption = useMemo(() => {
    if (!value?.continent) return null
    return {
      label: value.continent.ContinentName,
      continent: value.continent,
    }
  }, [value])

  return (
    <SingleItemAutocomplete<ContinentOption>
      sx={{ width: inputWidth }}
      label="Continent Name *"
      input={selectedOption?.label || ''}
      setInputValue={setInputValue}
      options={options}
      placeholder="Select a continent"
      hasError={error}
      getOptionLabel={(option: ContinentOption | string) =>
        typeof option === 'string' ? option : option.label
      }
      renderOption={(
        props: HTMLAttributes<HTMLLIElement> & { key: string },
        option: ContinentOption,
      ) => {
        const { key, ...otherProps } = props
        return (
          <Box
            component="li"
            key={key}
            {...otherProps}
            sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
          >
            <Typography>{option.label}</Typography>
            <Typography variant="caption" color="text.secondary">
              {option.continent.ContinentCode}
            </Typography>
          </Box>
        )
      }}
      isOptionEqualToValue={(
        option: ContinentOption,
        val: ContinentOption | string,
      ) => {
        if (typeof val === 'string') return option.label === val
        return option.continent.ContinentCode === val.continent.ContinentCode
      }}
      onChangeHandler={(_e, newValue) => {
        if (!newValue || typeof newValue === 'string') {
          onOptionChange(undefined)
          return
        }
        onOptionChange({
          continent: newValue.continent,
          continentName: newValue.label,
        })
      }}
      endAdornment={isLoading ? <CircularProgress size={20} /> : null}
    />
  )
}

interface CountryAutocompleteProps {
  value?: CountrySelection
  onOptionChange: (value: CountrySelection | undefined) => void
  error?: boolean
}

export function CountryAutocomplete({
  value,
  onOptionChange,
  error = false,
}: CountryAutocompleteProps) {
  const [inputValue, setInputValue] = useState(value?.countryName || '')

  const shouldFetch = !value?.country

  const { data: countries, isLoading } = useGetCountryLocations({
    countryName: shouldFetch ? inputValue : undefined,
  })

  const options: CountryOption[] = useMemo(() => {
    return (
      countries?.map((country) => ({
        label: country.CountryName,
        country,
      })) || []
    )
  }, [countries])

  const selectedOption = useMemo(() => {
    if (!value?.country) return null
    return {
      label: value.country.CountryName,
      country: value.country,
    }
  }, [value])

  return (
    <SingleItemAutocomplete<CountryOption>
      sx={{ width: inputWidth }}
      label="Country Name *"
      input={selectedOption?.label || ''}
      setInputValue={setInputValue}
      options={options}
      placeholder="Type 3 characters to search"
      hasError={error}
      getOptionLabel={(option: CountryOption | string) =>
        typeof option === 'string' ? option : option.label
      }
      renderOption={(
        props: HTMLAttributes<HTMLLIElement> & { key: string },
        option: CountryOption,
      ) => {
        const { key, ...otherProps } = props
        return (
          <Box
            component="li"
            key={key}
            {...otherProps}
            sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
          >
            <Typography>{option.label}</Typography>
            <Typography variant="caption" color="text.secondary">
              {option.country.CountryCode}
            </Typography>
          </Box>
        )
      }}
      isOptionEqualToValue={(
        option: CountryOption,
        val: CountryOption | string,
      ) => {
        if (typeof val === 'string') return option.label === val
        return option.country.CountryCode === val.country.CountryCode
      }}
      onChangeHandler={(_e, newValue) => {
        if (!newValue || typeof newValue === 'string') {
          onOptionChange(undefined)
          return
        }
        onOptionChange({
          country: newValue.country,
          countryName: newValue.label,
        })
      }}
      filterOptions={(x: CountryOption[]) => x}
      endAdornment={isLoading ? <CircularProgress size={20} /> : null}
    />
  )
}
